/**
 * Helpers for Revision Search Results: map IFS documents to table items, sort, layout, search params.
 */
import { getFileIconPath, getFileType, capitalizeFirstLetter, getDisplayFileName } from "../../../../functions/helperMethod";
import { getDocumentClassFormatLabel } from "../../../../functions/getDocumentClassFormatLabel";
import { formatDate } from "../../../../functions/dateFormater";
import CustomerConfig from "../../../../azure/CustomerConfig";

/**
 * Build search params for api/get-search (used by useDocumentSearch / documentsAPI.searchDocuments).
 * Revision search: fileStatus "'Checked In'", BASIC_SEARCH, optional date range.
 * @param {object} opts
 * @param {string} opts.searchValue - raw search text (from metadata or local state)
 * @param {number} [opts.pageIndex=0]
 * @param {number} [opts.pageSize]
 * @param {string} opts.tenantId
 * @param {string} opts.groupId
 * @param {string} opts.channelId
 * @param {string} [opts.advanceSearchOnlyUserInvolved]
 * @param {boolean} [opts.checkedOutToMe]
 * @returns {object} Params for searchDocuments / getSearchRecordsCount
 */
export function buildRevisionSearchParams({
  searchValue = "",
  pageIndex = 0,
  pageSize,
  tenantId,
  groupId,
  channelId,
  advanceSearchOnlyUserInvolved = "FALSE",
  checkedOutToMe = false,
}) {
  const config = new CustomerConfig();
  const size = pageSize ?? config.getPageLimit?.() ?? 20;
  const fromDate = new Date(new Date().setDate(new Date().getDate() - 90)).toLocaleString("en", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
  const toDate = new Date().toLocaleString("en", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
  return {
    searchValue: "'" + (searchValue ?? "") + "'",
    pageSize: size,
    pageIndex,
    tenantId: tenantId ?? "",
    groupId: groupId ?? "",
    channelId: channelId ?? "",
    searchType: "BASIC",
    advanceSearchReleaseLevel: "LATEST",
    advanceSearchStartDate: fromDate,
    advanceSearchEndDate: toDate,
    advanceSearchOnlyUserInvolved: advanceSearchOnlyUserInvolved === true || advanceSearchOnlyUserInvolved === "true" ? "TRUE" : "FALSE",
    advanceSearchAnyDate: true,
    advanceSearchDocClass: "''",
    advanceSearchFormat: "''",
    fileStatus: "'Checked In'",
    lastCheckOutMe: checkedOutToMe ?? false,
    docType: "''",
    isIfsFileAccessible: false,
  };
}

/**
 * Document number only — strips " - A2" / " - A3" suffix (revision search is LATEST).
 * @param {string} text
 * @returns {string}
 */
export function parseDocNoFromSearchText(text) {
  const q = String(text ?? "").trim();
  if (!q) return "";
  const match = q.match(/^(\d+)\s*-\s*.+$/i);
  if (match) return match[1].trim();
  return q;
}

/**
 * First dropdown row for a document number (IFS search uses release level LATEST).
 * @param {Array} items
 * @param {string} docNoOrText
 * @returns {object|null}
 */
export function findLatestDropdownItemForDocNo(items, docNoOrText) {
  const docNo = parseDocNoFromSearchText(docNoOrText);
  if (!docNo || !items?.length) return null;
  return (
    items.find(
      (row) => parseDocNoFromSearchText(row.docNo ?? row.documentNo) === docNo
    ) ?? null
  );
}

/**
 * @param {Array} documents - FilteredDocuments from IFS revision search
 * @returns {Array} Table row items
 */
export function generateDocuments(documents) {
  if (!documents?.length) return [];
  return documents
    .filter((item) => item.FileState !== "Checked Out")
    .map((item) => {
    return {
      key: item.Id,
      // Use shared OAS365 icons to match the AddoShare document dropdown.
      iconName: getFileIconPath(item.FileType, item),
      documentNo: item.docNo + " - " + item.docRev,
      title: getDisplayFileName(item.Title ?? ""),
      documentClassFormat: getDocumentClassFormatLabel(
        item.docClass,
        item.FormatSizeName
      ),
      documentBasic: `${item.docClass} - ${item.docNo} - ${item.docSheet} - ${item.docRev}`,
      titleInCapitalized: capitalizeFirstLetter(getDisplayFileName(item.Title ?? "")),
      checkedInDate: formatDate(item.CheckedInDate),
      checkedOutDate: formatDate(item.CheckedOutDate),
      fileSize: 0,
      sizeInbytes: item.FileSize,
      owner: item.DocRespName,
      class: item.docClass,
      className: item.DocClassName,
      format: item.FormatSize,
      formatName: item.FormatSizeName,
      sheetNo: item.docSheet,
      state: item.State,
      fileState: item.FileState,
      docNo: item.docNo,
      docRev: item.docRev,
      docType: item.docType,
      fileNo: item.fileNo,
      fileExtension: item.FileExtension,
      fileType: getFileType(item.FileExtension, item.FileType),
      lastActionDate: item.LatestHandling,
      lastAction: item.LastAction,
      viewStatus: item.ViewStatus,
      viewFileType: item.ViewFileType,
      ViewIconName:
        item.ViewFileExtension != null
          ? getFileIconPath(item.ViewFileType, {
              ...item,
              FileExtension: item.ViewFileExtension,
              FileType: item.ViewFileType,
            })
          : null,
      viewFileExtension: item.ViewFileExtension,
    };
  });
}

/**
 * @param {Array} items - Table items
 * @param {string} columnKey - fieldName to sort by
 * @param {boolean} isSortedDescending
 * @returns {Array} New sorted array
 */
export function copyAndSort(items, columnKey, isSortedDescending) {
  if (!items?.length) return items;
  const key = columnKey;
  if (key === "fileSize") {
    return [...items].sort((a, b) =>
      (isSortedDescending ? a.sizeInbytes < b.sizeInbytes : a.sizeInbytes > b.sizeInbytes) ? 1 : -1
    );
  }
  if (key === "title") {
    return [...items].sort((a, b) =>
      (isSortedDescending ? a.titleInCapitalized < b.titleInCapitalized : a.titleInCapitalized > b.titleInCapitalized)
        ? 1
        : -1
    );
  }
  return [...items].sort((a, b) =>
    (!isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1
  );
}

/**
 * @returns {number} Table height in px
 */
export function calculateTableHeight() {
  const screenHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  if (screenHeight < 600) return Math.max(screenHeight * 0.4, 200);
  if (screenHeight < 900) return screenHeight * 0.5;
  if (screenHeight < 1200) return screenHeight * 0.6;
  return Math.min(screenHeight * 0.65, 800);
}

/**
 * @param {number} tableHeight
 * @returns {{ overflowY: string, maxHeight: string, marginRight: string, marginLeft: string }}
 */
export function getRevisionGridStyle(tableHeight) {
  return {
    overflowY: "scroll",
    maxHeight: `${tableHeight}px`,
    marginRight: "10px",
    marginLeft: "0px",
  };
}
